import { FC, ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DatePicker, Empty, Space, TableProps, Typography } from 'antd';
import moment, { Moment } from 'moment';
import AppTable, { appTableStyles } from 'components/AppTable';
import { AppTableColumn } from 'components/AppTable/useColumns';
import { AppTableSlice } from 'components/AppTable/store';
import profileTableStyles from './profileTable.module.less';
import { ProfileDateRangeProps } from './useProfileDateRange';
import PeriodShiftButtons from './PeriodShiftButtons';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const DATE_FORMAT = 'YYYY-MM-DD';

export interface ReportColumn {
  title: string;
  dataIndex: string;
  width?: string | number;
  subtitle?: string;
  render?: (value: any, row: any) => ReactNode;
}

export interface ReportAverage {
  label: string;
  value?: string | null;
}

interface ReportTableProps extends ProfileDateRangeProps {
  storeSlice: AppTableSlice;
  columns: ReportColumn[];
  userUuid: string;
  userFilterKey?: string;
  countLabel?: string;
  averages?: ReportAverage[];
  rowKey?: string;
  expandable?: TableProps<any>['expandable'];
  onRenderData?: (data: Record<string, any>[]) => Record<string, any>[];
  applyRenderDataBeforePagination?: boolean;
  extraFilters?: ReactNode;
  headerContent?: ReactNode;
  ranges?: Record<string, [Moment, Moment]>;
}

const DEFAULT_RANGES: Record<string, [Moment, Moment]> = {
  'This month': [moment().startOf('month'), moment().endOf('month')],
  'Last 30 days': [moment().subtract(29, 'days'), moment()],
  'This week': [moment().startOf('week'), moment().endOf('week')],
};

const renderCell = (value: any) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const ReportTable: FC<ReportTableProps> = ({
  storeSlice,
  columns,
  userUuid,
  userFilterKey = 'responsible_user_uuid',
  countLabel = 'rows found',
  averages = [],
  rowKey,
  expandable,
  onRenderData,
  applyRenderDataBeforePagination,
  extraFilters,
  headerContent,
  ranges = DEFAULT_RANGES,
  dateRange,
  onDateRangeChange,
}) => {
  const dispatch = useDispatch();

  const startDate = dateRange[0].format(DATE_FORMAT);
  const endDate = dateRange[1].format(DATE_FORMAT);

  useEffect(() => {
    // Re-scope to the current user and the shared date range; setFilter refetches.
    dispatch(storeSlice.actions.setFixedParams({ [userFilterKey]: userUuid }));
    dispatch(
      storeSlice.actions.setFilter({
        date_start: { value: startDate },
        date_end: { value: endDate },
      }),
    );
  }, [dispatch, storeSlice, userUuid, userFilterKey, startDate, endDate]);

  const tableColumns = columns.map((col) => ({
    title: col.subtitle ? (
      <Space size={6}>
        <span>{col.title}</span>
        <Text type="secondary" style={{ fontWeight: 'normal' }}>
          {col.subtitle}
        </Text>
      </Space>
    ) : (
      col.title
    ),
    dataIndex: col.dataIndex,
    key: col.dataIndex,
    width: col.width,
    render: col.render ?? renderCell,
  })) as unknown as AppTableColumn[];

  return (
    <div className={appTableStyles.container} style={{ padding: 0 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap align="center">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {headerContent}

          {averages.length > 0 && (
            <div style={{ display: 'flex', gap: 12 }}>
              {averages.map((avg) => (
                <div
                  key={avg.label}
                  style={{
                    border: '1px solid rgba(0, 68, 204, 0.2)',
                    borderRadius: 3,
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{avg.label}</span>
                  <span>~{avg.value ?? '--'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <PeriodShiftButtons dateRange={dateRange} onDateRangeChange={onDateRangeChange} />
          <RangePicker
            size="large"
            allowClear={false}
            format="MM/DD/YYYY"
            value={dateRange}
            ranges={ranges}
            onChange={(dates: any) => {
              if (dates && dates[0] && dates[1]) {
                onDateRangeChange([dates[0], dates[1]]);
              }
            }}
          />

          {extraFilters}
        </div>
      </Space>

      <AppTable
        storeSlice={storeSlice}
        className={profileTableStyles.table}
        columns={tableColumns}
        rowKey={rowKey}
        textCount={countLabel}
        prefixPagination="Total"
        emptyText={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />}
        expandable={expandable}
        onRenderData={onRenderData}
        applyRenderDataBeforePagination={applyRenderDataBeforePagination}
        hideRowSelection
        hideActionColumn
        autoHeight
      />
    </div>
  );
};

export default ReportTable;
